import { Option, pipe, ReadonlyArray } from "fp";
import { transparentize } from "polished";
import React from "react";
import styled from "styled-components";

import * as Color from "~/Color";
import { Company } from "~/Company";
import * as Data from "~/Data";
import * as Driver from "~/Driver";
import * as Popover from "~/Popover";
import { Time } from "~/Time";
import { Timeline } from "~/Timeline";
import * as Typography from "~/Typography";

export type Markers = readonly Marker[];
export function Markers({
  timeline,
  milestones = [Milestone.toMilestone({ type: "Local", ...Time.Zone.local() })],
  bottomLabelOnly,
  containerWidth,
}: {
  readonly timeline: Timeline.Remote;
  readonly milestones?: readonly Milestone.Timezone[];
  readonly bottomLabelOnly?: boolean;
  readonly containerWidth?: number;
  readonly targetContainer?: React.RefObject<HTMLDivElement>;
  readonly screenWidth?: number;
}) {
  const includeMarker = (index: number, width?: number, isBottomOnly = false) =>
    width
      ? isBottomOnly
        ? width < 1366
          ? index % 3 === 0
          : true
        : width < 1700
        ? index % 2 === 0
        : true
      : true;

  return Data.Remote.when(timeline, {
    loading: () => null,
    error: () => null,
    success: (timeline) => {
      return pipe(
        Markers.fromTimeline(timeline),
        ReadonlyArray.mapWithIndex((index, marker) => (
          <Marker
            key={index}
            timeline={timeline}
            marker={marker}
            milestones={milestones}
            bottomLabelOnly={bottomLabelOnly}
            pixelDensity={100}
          />
        )),
        ReadonlyArray.filterWithIndex((index) =>
          includeMarker(index, containerWidth, bottomLabelOnly)
        ),
        (markers) => (
          <Markers.Container {...timeline}>{markers}</Markers.Container>
        )
      );
    },
  });
}

export namespace Markers {
  // TODO: Here
  export const fromTimeline = (timeline: Timeline): Markers =>
    pipe(
      ReadonlyArray.replicate(count(timeline), { timeline }),
      ReadonlyArray.mapWithIndex((index, { timeline }) => ({
        dateTime:
          timeline.interval.start.dateTime + index * durationBetween().duration,
      })),
      (markers) =>
        timeline.now && Time.Interval.Stopped.contains(timeline.now)(timeline)
          ? [...markers, timeline.now]
          : markers
    );

  export const durationBetween = (): Time.Duration => Time.Duration.hours(1);

  const count = (timeline: Timeline): number =>
    Timeline.pixelsTotal(timeline) /
    (durationBetween().duration * Timeline.pixelsPerMillisecond());

  export const Container = styled.div<Timeline>`
    position: absolute;
    left: 0;
    right: 0;
    height: inherit;
    top: -${() => Marker.height()};
    bottom: -${() => Marker.height()};
    overflow: hidden;
  `;
}

export type Marker = Time.DateTime;
export function Marker(props: Marker.Props) {
  return (
    <Marker.Container {...props}>
      {!props.bottomLabelOnly && <Marker.Label {...props} isTop={true} />}
      {!props.bottomLabelOnly &&
      Marker.isMarkerStartOfDayForZone(props.milestones)(props.marker) ? (
        <Milestone.VerticalLine {...props} />
      ) : (
        <Marker.VerticalLine {...props} />
      )}
      <Marker.Label {...props} isTop={false} />
    </Marker.Container>
  );
}

export namespace Marker {
  export type BrusherMarkerOnly = {
    readonly bottomLabelOnly?: boolean;
  };
  export type Props = BrusherMarkerOnly & {
    readonly timeline: Timeline;
    readonly marker: Marker;
    readonly milestones: readonly Milestone.Timezone[];
    readonly pixelDensity: number;
  };

  export type LabelProps = Props & {
    readonly isTop: boolean;
  };

  export const isMarkerNow =
    (timeline: Timeline) =>
    (marker: Marker): boolean =>
      pipe(
        Option.fromNullable(timeline.now),
        Option.map((now) => now === marker),
        Option.getOrElse(() => false as boolean)
      );

  export const isMarkerStartOfDayForZone =
    (milestones: readonly Milestone.Timezone[]) =>
    (marker: Marker): boolean =>
      milestones.some((tz) => Time.DateTime.isStartOfDayForZone(marker, tz));

  export const isNowClose =
    (timeline: Timeline) =>
    (marker: Marker): boolean =>
      pipe(
        Option.fromNullable(timeline.now),
        Option.fold(
          () => false,
          (now) =>
            Time.Interval.Stopped.contains(now)(timeline) &&
            Time.Interval.Stopped.contains(now)({
              interval: {
                start: {
                  dateTime:
                    marker.dateTime - Markers.durationBetween().duration / 2,
                },
                stop: {
                  dateTime:
                    marker.dateTime + Markers.durationBetween().duration / 2,
                },
              },
            })
        )
      );

  export const width = () => "1px" as const;
  export const height = () => "1.5em" as const;
  export const fat = () => "2px" as const;

  export const Container = styled.div<Props>`
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${({ timeline, marker }) =>
      Timeline.percentageFromDateTime(timeline)(marker) * 100}%;
    right: ${({ timeline, marker }) =>
      Timeline.percentageToDateTime(timeline)(marker) * 100}%;
    min-width: 24px;

    & > div {
      display: flex;
      flex-direction: column;

      &:first-of-type {
        top: 0;
        justify-content: flex-start;
      }

      &:last-of-type {
        bottom: 0;
        justify-content: flex-end;
      }
    }
  `;

  type VerticalLineProps = Props & {
    readonly isFat?: boolean;
    readonly color?: Color.Color;
  };

  export const VerticalLine = styled.hr<VerticalLineProps>`
    position: absolute;
    top: 0;
    bottom: 0;
    transform: translateX(
      calc(${({ isFat = false }) => (isFat ? fat() : width())} / -2)
    );
    height: auto;
    overflow: ${({ bottomLabelOnly }) =>
      bottomLabelOnly ? "visible" : "hidden"};
    background-color: transparent;
    border: none;
    border-left: ${({ isFat = false }) => (isFat ? fat() : width())} dashed
      ${({ timeline, marker, color = Color.veryWhiteGrey() }) =>
        isMarkerNow(timeline)(marker)
          ? transparentize(0.5)(Color.azure())
          : color};
    width: auto;
  `;

  export const pixelsPerHour = (width: number, hours: number): number =>
    width / hours;

  const formatTimeLabel = (
    marker: Marker,
    defaultZone: Time.Zone,
    small = false
  ) => {
    return Time.DateTime.isStartOfDayForZone(marker, defaultZone) ||
      Time.DateTime.isEndOfDayForZone(marker, defaultZone)
      ? small
        ? Time.DateTime.toDayNameOnly(marker, defaultZone)
        : Time.DateTime.toDayName(marker, defaultZone)
      : null;
  };

  const generateTimeLabel = (
    marker: Marker,
    isTop = false,
    isBottomOnly = false
  ) => {
    const defaultZone = Time.Zone.use();
    const smallDayLabel = formatTimeLabel(marker, defaultZone, true);
    if (isBottomOnly) {
      const timeLabel = smallDayLabel
        ? smallDayLabel
        : Time.DateTime.toHa(marker, defaultZone);

      return (
        <Label.Time shouldWrap={false}>
          <Label.TimeItem
            key={1}
            grow={1}
            shrink={1}
            basis={"auto"}
            order={0}
            padding="0"
          >
            {timeLabel}
          </Label.TimeItem>
        </Label.Time>
      );
    }

    if (smallDayLabel) {
      return (
        <Label.Time shouldWrap={false}>
          <Label.TimeItem
            key={1}
            grow={0}
            shrink={0}
            basis={"auto"}
            order={0}
            padding="0"
          >
            {smallDayLabel}
          </Label.TimeItem>
        </Label.Time>
      );
    }

    const label = Time.DateTime.toList(marker, defaultZone);
    const parts = label.split(",");

    return (
      <Label.Time shouldWrap={!isTop}>
        <Label.TimeItem
          key={1}
          grow={0}
          shrink={0}
          basis={"auto"}
          order={-1}
          padding="0"
        >
          {parts[0]}
        </Label.TimeItem>
        <Label.DissolvingTimeItem key={2}>:{parts[1]}</Label.DissolvingTimeItem>
        <Label.TimeItem
          key={3}
          grow={0}
          shrink={0}
          basis={"auto"}
          order={0}
          padding="0"
        >
          {parts[2].substr(0, 1)}
        </Label.TimeItem>
        <Label.TimeItem
          key={4}
          grow={4}
          shrink={4}
          basis={"auto"}
          order={0}
          padding="0"
        >
          M
        </Label.TimeItem>
      </Label.Time>
    );
  };

  export function Label({
    timeline,
    marker,
    isTop,
    bottomLabelOnly,
  }: LabelProps) {
    return (
      <Label.Container bottomLabelOnly={bottomLabelOnly}>
        <Time.DateTime
          {...marker}
          Time={() =>
            isMarkerNow(timeline)(marker) ? (
              <Label.Now strong>
                {generateTimeLabel(marker, isTop, bottomLabelOnly)}
              </Label.Now>
            ) : !isNowClose(timeline)(marker) ? (
              <Typography.Component muted>
                {generateTimeLabel(marker, isTop, bottomLabelOnly)}
              </Typography.Component>
            ) : null
          }
          Separator={() => null}
          Date={() => null}
        />
      </Label.Container>
    );
  }

  export namespace Label {
    type LabelProps = BrusherMarkerOnly;

    export const Container = styled.div<LabelProps>`
      position: ${({ bottomLabelOnly }) =>
        bottomLabelOnly ? "relative" : "absolute"};
      left: 0.5em;
      right: 0.5em;
      height: ${() => height()};
      white-space: nowrap;
      overflow: ${({ bottomLabelOnly }) =>
        bottomLabelOnly ? "visible" : "hidden"};
      font-size: ${() => Typography.FontSize.data()};
    `;

    export const Now = styled(Typography.Component)`
      color: ${() => Color.azure()};
    `;

    type TimeProps = {
      readonly shouldWrap: boolean;
    };

    export const Time = styled.div<TimeProps>`
      display: flex;
      flex-direction: row;
      flex-wrap: ${({ shouldWrap }) => (shouldWrap ? "wrap-reverse" : "wrap")};
      justify-content: flex-start;
      min-height: ${() => height()};
      white-space: nowrap;
      width: 100%;
      overflow: ${({ shouldWrap }) => (shouldWrap ? "visible" : "hidden")};
      margin-right: ${({ shouldWrap }) => (shouldWrap ? "0" : "15%")};
    `;

    export type ItemProps = {
      readonly grow: number;
      readonly shrink: number;
      readonly basis: string;
      readonly order: number;
      readonly padding: string;
    };

    export const TimeItem = styled.div<ItemProps>`
      display: inline-block;
      height: ${() => height()};
      flex: ${({ grow }) => grow} ${({ shrink }) => shrink}
        ${({ basis }) => basis};
      order: ${({ order }) => order};
      white-space: nowrap;
      margin: 0;
      margin-right: ${({ padding }) => padding};
      padding: 0;
      min-width: 0;
    `;

    export const DissolvingTimeItem = styled.div`
      display: inline-block;
      height: ${() => height()};
      white-space: nowrap;
      min-width: 21px;
      max-width: 100%;
      width: calc((50px - 100%) * 50);
      padding-right: 0.4em;
    `;
  }
}

export namespace Milestone {
  export type TimezoneType = {
    readonly type: "Company" | "Driver" | "Local";
  };

  export type Timezone = TimezoneType & Time.Zone;

  export const toMilestone = ({ type, zone }: Timezone) => ({
    type,
    zone,
  });

  export function VerticalLine(props: Marker.Props) {
    const driverMilestone = props.milestones.find(
      ({ type }) => type === "Driver"
    );
    if (
      driverMilestone &&
      Marker.isMarkerStartOfDayForZone([driverMilestone])(props.marker)
    ) {
      return (
        <Line
          {...props}
          milestone={driverMilestone}
          Title={() => (
            <Typography.H4.Component>
              Start of Driver's Day (12 AM)
            </Typography.H4.Component>
          )}
          Content={() => <WRLMilestoneMessage />}
        />
      );
    }

    return <Marker.VerticalLine {...props} />;
  }

  const milestoneTypeToColor = ({ type }: TimezoneType) =>
    type === "Driver"
      ? Color.translucentColor(Color.green(), 0.6)
      : Color.veryWhiteGrey();

  type MilesStoneLineProps = Marker.Props & {
    readonly milestone: Timezone;
    readonly Title?: Component;
    readonly Content: Component;
  };
  export const Line = (props: MilesStoneLineProps) => (
    <>
      <Marker.VerticalLine
        isFat={true}
        color={milestoneTypeToColor(props.milestone)}
        {...props}
      />
      <Top {...props.milestone} />
      <Bottom {...props.milestone} />
      <Hover {...props} />
    </>
  );

  export const Top = styled.div<Timezone>`
    position: absolute;
    left: -5px;
    top: 0;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 10px solid ${({ type }) => milestoneTypeToColor({ type })};
  `;

  export const Bottom = styled.div<Timezone>`
    position: absolute;
    bottom: 0;
    left: -5px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 10px solid ${({ type }) => milestoneTypeToColor({ type })};
  `;

  export type Hover = {
    readonly Title?: Component;
    readonly Content: Component;
  };

  export function Hover({ Title, Content }: Hover) {
    return (
      <Popover.Component
        placement="right"
        Title={() =>
          Title ? (
            <Hover.Container>
              <Title />
            </Hover.Container>
          ) : null
        }
        Content={() => (
          <Hover.Container>
            <Content />
          </Hover.Container>
        )}
      >
        <Milestone.Hover.Trigger />
      </Popover.Component>
    );
  }

  export const WRLMilestoneMessage = () => {
    const homeOfficeLocation = Driver.useFromReference({
      ...Option.toUndefined(Driver.useReferenceFromRoute()),
      found: (driver) => (driver?.homeOffice ? driver?.homeOffice : null),
      missing: () => null,
    });

    return (
      <>
        <Typography.Component muted>
          Based on the Driver's time zone
        </Typography.Component>
        {homeOfficeLocation && (
          <>
            <Typography.Component muted>&nbsp;at&nbsp;</Typography.Component>
            <Company.Location {...homeOfficeLocation} />
          </>
        )}
      </>
    );
  };

  export namespace Hover {
    export const Container = styled.div`
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    `;

    export const Trigger = styled.div`
      position: absolute;
      top: 0;
      bottom: 0;
      left: -5px;
      background-color: transparent;
      min-width: 10px;
      height: auto;
      width: 100%;
      z-index: 10;
    `;
  }
}
